/**
 * Persistencia IndexedDB para la cola offline de pedidos POSKN.
 * Fallback a localStorage si IDB no está disponible.
 */

export const COLA_STORAGE_KEY = 'pos_pedidos_pendientes_subir';
export const COLA_STORAGE_BAK_KEY = 'pos_pedidos_pendientes_subir_bak';
export const COLA_MIGRATION_FLAG = 'poskn_queue_idb_migrated_v1';
export const COLA_MAX_ITEMS = 500;

const DB_NAME = 'poskn_pos_v1';
const DB_VERSION = 1;
const STORE = 'pending_orders';

let dbPromise = null;
let queueCache = null;
let initPromise = null;
let useIndexedDb = true;
/** Encadena escrituras para que un guardado viejo no pise un anular/vaciar. */
let persistChain = Promise.resolve();
let latestPersistList = null;

function idbSupported(win) {
	return !!(win && win.indexedDB);
}

function openDb() {
	if (!useIndexedDb) {
		return Promise.reject(new Error('IndexedDB deshabilitado'));
	}
	if (dbPromise) return dbPromise;
	dbPromise = new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, DB_VERSION);
		req.onupgradeneeded = (event) => {
			const db = event.target.result;
			if (!db.objectStoreNames.contains(STORE)) {
				const store = db.createObjectStore(STORE, { keyPath: 'client_pedido_id' });
				store.createIndex('fechaGuardado', 'fechaGuardado', { unique: false });
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error || new Error('No se pudo abrir IndexedDB'));
	});
	return dbPromise;
}

function ensureClientId(item) {
	if (!item || typeof item !== 'object') return '';
	const pc = item.pedidoCompleto;
	return (
		item.client_pedido_id ||
		(pc && pc.client_pedido_id) ||
		`legacy-${item.fechaGuardado || Date.now()}-${Math.random().toString(36).slice(2, 9)}`
	);
}

export function normalizeQueueItem(item) {
	if (!item || typeof item !== 'object') return null;
	const pc = item.pedidoCompleto;
	const cid = item.client_pedido_id || (pc && pc.client_pedido_id) || '';
	if (!cid && !pc) return null;
	const normalized = { ...item };
	normalized.client_pedido_id = ensureClientId(normalized);
	if (pc && !pc.client_pedido_id) {
		normalized.pedidoCompleto = { ...pc, client_pedido_id: normalized.client_pedido_id };
	}
	return normalized;
}

function trimQueue(arr) {
	// Nunca descartar ventas antiguas: si hay overflow se rechaza el alta en guardarEnCola.
	return Array.isArray(arr) ? arr.map(normalizeQueueItem).filter(Boolean) : [];
}

function sortQueue(items) {
	return items.slice().sort((a, b) => {
		const fa = a.fechaGuardado || '';
		const fb = b.fechaGuardado || '';
		return fa.localeCompare(fb);
	});
}

function parseQueueJson(raw) {
	if (!raw) return [];
	try {
		const q = JSON.parse(raw);
		if (!Array.isArray(q)) return [];
		return sortQueue(trimQueue(q));
	} catch (e) {
		return [];
	}
}

function readLocalStorage(win) {
	const primary = parseQueueJson(win.localStorage.getItem(COLA_STORAGE_KEY));
	const bak = parseQueueJson(win.localStorage.getItem(COLA_STORAGE_BAK_KEY));
	if (!bak.length) return primary;
	if (!primary.length) return bak;
	const merged = new Map();
	primary.forEach((item) => {
		const row = normalizeQueueItem(item);
		if (row) merged.set(row.client_pedido_id, row);
	});
	bak.forEach((item) => {
		const row = normalizeQueueItem(item);
		if (row && !merged.has(row.client_pedido_id)) {
			merged.set(row.client_pedido_id, row);
		}
	});
	return sortQueue(Array.from(merged.values()));
}

function writeLocalStorage(win, arr, notifyTrim) {
	const list = trimQueue(arr);
	let payload = JSON.stringify(list);
	const writeBoth = (data) => {
		win.localStorage.setItem(COLA_STORAGE_KEY, data);
		try {
			if (!data || data === '[]') {
				win.localStorage.removeItem(COLA_STORAGE_BAK_KEY);
			} else {
				win.localStorage.setItem(COLA_STORAGE_BAK_KEY, data);
			}
		} catch (eBak) {
			/* ignore */
		}
	};
	try {
		writeBoth(payload);
		return { ok: true, list, trimmed: false };
	} catch (e) {
		console.error('No se pudo respaldar cola en localStorage (IndexedDB se mantiene):', e);
		if (notifyTrim) {
			notifyTrim('Cola offline en IndexedDB. localStorage sin espacio; no se recortó ningún pedido.');
		}
		return { ok: false, list, trimmed: false };
	}
}

/** Respaldo síncrono en localStorage para no perder ventas si se recarga antes de IndexedDB. */
export function backupQueueToLocalStorage(win, arr, notifyTrim) {
	return writeLocalStorage(win, Array.isArray(arr) ? arr : [], notifyTrim);
}

async function idbGetAll() {
	const db = await openDb();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE, 'readonly');
		const req = tx.objectStore(STORE).getAll();
		req.onsuccess = () => resolve(sortQueue(trimQueue(req.result || [])));
		req.onerror = () => reject(req.error);
	});
}

async function idbSaveAll(items) {
	const db = await openDb();
	const list = trimQueue(items);
	await new Promise((resolve, reject) => {
		const tx = db.transaction(STORE, 'readwrite');
		const store = tx.objectStore(STORE);
		store.clear();
		list.forEach((item) => {
			const row = normalizeQueueItem(item);
			if (row && row.client_pedido_id) store.put(row);
		});
		tx.oncomplete = () => resolve(true);
		tx.onerror = () => reject(tx.error);
	});
	const saved = await idbGetAll();
	const want = new Set(list.map((item) => item && item.client_pedido_id).filter(Boolean));
	const got = new Set(saved.map((item) => item && item.client_pedido_id).filter(Boolean));
	if (want.size !== got.size) {
		throw new Error('Cola IDB incompleta tras guardar');
	}
	want.forEach((id) => {
		if (!got.has(id)) {
			throw new Error('Cola IDB sin pedido ' + id);
		}
	});
	return list;
}

async function migrateFromLocalStorage(win) {
	if (win.localStorage.getItem(COLA_MIGRATION_FLAG) === '1') return;
	const legacy = readLocalStorage(win);
	if (legacy.length && useIndexedDb) {
		try {
			await idbSaveAll(legacy);
		} catch (e) {
			console.warn('Migración cola → IndexedDB falló, se mantiene localStorage:', e);
			return;
		}
	}
	try {
		win.localStorage.setItem(COLA_MIGRATION_FLAG, '1');
		// Se conserva localStorage como respaldo ante recarga / fallo de IndexedDB.
	} catch (e) {
		/* ignore */
	}
}

/**
 * Carga la cola desde IndexedDB (o localStorage) una sola vez al arrancar.
 *
 * @param {Window} win
 * @returns {Promise<object[]>}
 */
export function initQueueStorage(win) {
	if (initPromise) return initPromise;
	initPromise = (async () => {
		useIndexedDb = idbSupported(win);
		if (useIndexedDb) {
			try {
				await migrateFromLocalStorage(win);
				const loaded = await idbGetAll();
				const fromLs = readLocalStorage(win);
				const merged = new Map();
				loaded.forEach((item) => {
					const row = normalizeQueueItem(item);
					if (row) merged.set(row.client_pedido_id, row);
				});
				fromLs.forEach((item) => {
					const row = normalizeQueueItem(item);
					if (row) merged.set(row.client_pedido_id, row);
				});
				if (Array.isArray(queueCache) && queueCache.length > 0) {
					queueCache.forEach((item) => {
						const row = normalizeQueueItem(item);
						if (row) merged.set(row.client_pedido_id, row);
					});
				}
				queueCache = sortQueue(Array.from(merged.values()));
				if (queueCache.length) {
					await idbSaveAll(queueCache);
				}
				return queueCache;
			} catch (e) {
				console.warn('IndexedDB no disponible, usando localStorage:', e);
				useIndexedDb = false;
				dbPromise = null;
			}
		}
		queueCache = readLocalStorage(win);
		return queueCache;
	})();
	return initPromise;
}

export function getQueueSnapshot() {
	if (Array.isArray(queueCache)) {
		return queueCache.map(normalizeQueueItem).filter(Boolean);
	}
	return [];
}

/** true cuando initQueueStorage ya cargó (incluye cola vacía). */
export function isQueueInitialized() {
	return Array.isArray(queueCache);
}

/**
 * Persistencia serializada: un anular/vaciar nunca es pisado por un write anterior.
 *
 * @param {Window} win
 * @param {object[]} arr
 * @param {(msg: string) => void} [onTrimWarning]
 * @returns {Promise<{ ok: boolean, list: object[], skipped?: boolean }>}
 */
export function persistQueueStorage(win, arr, onTrimWarning) {
	const incoming = Array.isArray(arr) ? arr : [];
	const list = trimQueue(incoming);
	latestPersistList = list;
	queueCache = list;

	const run = async () => {
		// Coalesce: si hubo otro setCola más nuevo, este write no aplica.
		await Promise.resolve();
		if (latestPersistList !== list) {
			return { ok: true, list: getQueueSnapshot(), skipped: true };
		}

		let saved = list;
		let ok = true;

		if (useIndexedDb) {
			try {
				saved = await idbSaveAll(list);
				if (latestPersistList !== list) {
					return { ok: true, list: getQueueSnapshot(), skipped: true };
				}
				try {
					if (saved.length === 0) {
						win.localStorage.removeItem(COLA_STORAGE_KEY);
						win.localStorage.removeItem(COLA_STORAGE_BAK_KEY);
					} else {
						writeLocalStorage(win, saved, onTrimWarning);
					}
				} catch (eSync) {
					/* ignore */
				}
			} catch (e) {
				console.warn('Error guardando cola en IndexedDB, fallback localStorage:', e);
				useIndexedDb = false;
				dbPromise = null;
				const ls = writeLocalStorage(win, list, onTrimWarning);
				ok = ls.ok;
				saved = ls.list;
			}
		} else {
			const ls = writeLocalStorage(win, list, onTrimWarning);
			ok = ls.ok;
			saved = ls.list;
		}

		if (latestPersistList !== list) {
			return { ok: true, list: getQueueSnapshot(), skipped: true };
		}

		queueCache = saved;
		return { ok, list: saved };
	};

	persistChain = persistChain.then(run, run);
	return persistChain;
}

/** Persistencia síncrona de respaldo mientras IDB aún no terminó de escribir. */
export function setQueueCacheMemory(arr) {
	const list = trimQueue(arr);
	queueCache = list;
	latestPersistList = list;
}
